import { useUpload } from "@/api/files";
import { client } from "@/hono";
import { BlankSlateAIScan } from "@msuite/ui/blank-slate";
import { FileUpload, useFileUpload } from "@msuite/ui/file-upload";
import { useLoading } from "@msuite/ui/hooks";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { useDataTableContext } from "@msuite/ui/table";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { FormProvider, useForm } from "react-hook-form";

/** Props Interface */
interface AddCalculationModalProps extends ModalProps {}

export function AddCalculationModal({ isOpen, onClose }: AddCalculationModalProps) {
	/** Hooks */
	const table = useDataTableContext();
	const { t } = useTranslation();
	const methods = useForm();
	const { uploadFn } = useUpload();
	const controller = useFileUpload({ uploadFn });
	const extractLoader = useLoading();

	/** Functions */
	function validateFiles(): boolean {
		const files = controller.files;
		const hasX84 = files.some((f) => /\.x84$/i.test(f.name));
		const hasX83 = files.some((f) => /\.x83$/i.test(f.name));
		const hasD84 = files.some((f) => /\.d84$/i.test(f.name));
		const hasD83 = files.some((f) => /\.d83$/i.test(f.name));
		if ((hasX84 && !hasX83) || (hasD84 && !hasD83)) {
			toast.error(t("calculations.offer_missing_x83"));
			return false;
		}
		return true;
	}

	async function handleOnSubmit() {
		if (!validateFiles()) return;
		try {
			const uploadResults = await controller.handleOnFileUpload();
			extractLoader.start();
			const response = await client.calculations.offers.$post({ json: { uploadResults } });
			await responseHandler(response);
			controller.handleOnReset();
			onClose();
			await table?.refetch?.();
		} catch (error) {
			console.error(error);
		} finally {
			extractLoader.stop();
		}
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("calculations.add_calculation")}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
			>
				<Modal.Body>
					{extractLoader.isLoading ? (
						<BlankSlateAIScan />
					) : (
						<FileUpload
							controller={controller}
							withLabel={false}
							renderFile={() => <></>}
							accept={{
								"application/pdf": [".pdf"],
								"application/xml": [".x84", ".x83"],
								"text/xml": [".x84", ".x83"],
								"application/octet-stream": [".x84", ".x83", ".d84", ".d83"],
							}}
							hint={t("calculations.offer_upload_hint")}
						/>
					)}
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
